// Sugalarm — voice command DIAGNOSTIC (cycle 385). ANDROID KIOSK ONLY.
//
// "What did you hear, and what did you make of it?" — one recognition pass
// that reports the transcript and the matched intent and then STOPS.
//
// ─── WHY THIS IS A SEPARATE HOOK, NOT A MODE ON useVoiceCommands ───────
// `useVoiceCommands` is the safety-critical session machine: it speaks, it
// writes treatments, it owns the confirm window, and cycle 383 shipped two
// review-caught defects in its speech lifecycle before it was right.
// Adding a "diagnostic mode" flag to it would put a test path through the
// same code that logs insulin. This hook instead reuses only the two thin,
// side-effect-free pieces — the native bridge and the pure grammar — so
// there is NO code path from here to a dose, an utterance, or the alarm.
//
// HARD PROPERTIES, and the reason the card can be shown without ceremony:
//   • It NEVER dispatches an intent. It parses and reports, nothing else.
//   • It NEVER speaks (no TTS import at all).
//   • It NEVER writes a treatment (no api import at all).
//   • It NEVER touches alarm state — it only READS `alarmLive` to refuse.
//   • The transcript stays in React state on the device and is not sent,
//     logged, or persisted anywhere (cycle-383 transcript hygiene).

import { useCallback, useEffect, useRef, useState } from "react";

import * as voiceCapture from "../lib/voiceCapture";
import * as wakeWord from "../lib/wakeWord";
import type { VoiceErrorCode } from "../lib/voiceCapture";
import type { VoiceIntent } from "../lib/voiceGrammar";
import { parseTranscript } from "../lib/voiceGrammar";

export type VoiceTestPhase = "idle" | "listening" | "result";

export interface VoiceTestOutcome {
  /** Exactly what the recognizer returned. Shown on-device only. */
  transcript: string | null;
  /** What the grammar made of it — `null` means "not recognized", which is
   *  a legitimate, informative answer, not a failure. */
  intent: VoiceIntent | null;
  /** Set when the recognizer failed instead of returning a transcript. */
  error: VoiceErrorCode | null;
}

export interface UseVoiceTestOpts {
  /** Gate: the card is mounted (kiosk + dev flag + plugin present). */
  enabled: boolean;
  /** `episode.isLive` — READ ONLY. Voice defers to a sounding alarm. */
  alarmLive: boolean;
}

export interface VoiceTestState {
  phase: VoiceTestPhase;
  /** Live partial transcript while listening — the thing that makes this
   *  card worth having: you can watch it hear you, word by word. */
  partial: string | null;
  outcome: VoiceTestOutcome | null;
  /** True once the native recognizer has been confirmed usable. */
  available: boolean;
  start: () => void;
  stop: () => void;
}

export function useVoiceTest(opts: UseVoiceTestOpts): VoiceTestState {
  const { enabled, alarmLive } = opts;

  const [available, setAvailable] = useState(false);
  const [phase, setPhase] = useState<VoiceTestPhase>("idle");
  const [partial, setPartial] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<VoiceTestOutcome | null>(null);

  const phaseRef = useRef<VoiceTestPhase>("idle");
  const setPhaseBoth = useCallback((p: VoiceTestPhase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  const alarmLiveRef = useRef(alarmLive);
  alarmLiveRef.current = alarmLive;

  // Probe once, only when the card is actually mounted.
  useEffect(() => {
    if (!enabled) {
      setAvailable(false);
      return;
    }
    let cancelled = false;
    void voiceCapture.isAvailable().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const finish = useCallback(
    (o: VoiceTestOutcome) => {
      setOutcome(o);
      setPartial(null);
      setPhaseBoth("result");
      void voiceCapture.stopListening();
      void wakeWord.resume();
    },
    [setPhaseBoth],
  );

  const finishRef = useRef(finish);
  finishRef.current = finish;

  // Native events. Subscribed only while the card is mounted; the handler
  // ignores anything that arrives outside a test we started, so it can
  // never observe (or interfere with) a real `useVoiceCommands` session.
  useEffect(() => {
    if (!enabled) return;
    return voiceCapture.subscribe({
      onPartial: (t) => {
        if (phaseRef.current === "listening") setPartial(t);
      },
      onFinal: (t) => {
        if (phaseRef.current !== "listening") return;
        finishRef.current({
          transcript: t,
          intent: parseTranscript(t),
          error: null,
        });
      },
      onError: (code) => {
        if (phaseRef.current !== "listening") return;
        finishRef.current({ transcript: null, intent: null, error: code });
      },
    });
  }, [enabled]);

  const start = useCallback(() => {
    if (!enabled || !available) return;
    // Voice defers to a sounding alarm, exactly as the real session does.
    if (alarmLiveRef.current) return;
    if (phaseRef.current === "listening") return;
    setOutcome(null);
    setPartial(null);
    void (async () => {
      // Ask the wake service for the microphone first — the bench sequence
      // (start the service, then run a test) IS the contention case, and
      // two capture clients cannot share the mic.
      await wakeWord.yieldMic();
      setPhaseBoth("listening");
      await voiceCapture.startListening();
    })();
  }, [enabled, available, setPhaseBoth]);

  const stop = useCallback(() => {
    void voiceCapture.stopListening();
    void wakeWord.resume();
    setPartial(null);
    setPhaseBoth("idle");
  }, [setPhaseBoth]);

  // An alarm firing mid-test cancels it immediately — same rule as the
  // real session, so a test can never hold the microphone through an alarm.
  useEffect(() => {
    if (!alarmLive) return;
    if (phaseRef.current !== "listening") return;
    void voiceCapture.stopListening();
    void wakeWord.resume();
    setPartial(null);
    setPhaseBoth("idle");
  }, [alarmLive, setPhaseBoth]);

  // Leaving the card (or the screen) must never leave the mic open.
  useEffect(() => {
    return () => {
      void voiceCapture.stopListening();
      void wakeWord.resume();
    };
  }, []);

  return { phase, partial, outcome, available, start, stop };
}
